// Pure helpers for the DataTable plugin: cell normalization, row/column edits,
// CSV parsing and resolving the effective display options. No React, no i18n —
// unit-tested in plugins/DataTable/__tests__/tableData.test.js.

// Legacy state stores cells/headers wrapped in arrays (`[["Ronald"]]`, see the
// old getInitialState); new edits store plain strings. Read both.
export function normalizeCell(cell) {
  if (cell === null || cell === undefined) return "";
  if (Array.isArray(cell)) return normalizeCell(cell[0]);
  return String(cell);
}

// -> { keys: string[], rows: string[][] } with every row padded/truncated to the
// header width. Always at least one column.
export function normalizeTable(data, keys) {
  const headerSrc = Array.isArray(keys) ? keys : [];
  const rowSrc = Array.isArray(data) ? data : [];
  const width = Math.max(
    1,
    headerSrc.length,
    ...rowSrc.map((r) =>
      Array.isArray(r) ? r.length : r && typeof r === "object" ? Object.keys(r).length : 0
    )
  );
  const outKeys = [];
  for (let c = 0; c < width; c++) outKeys.push(normalizeCell(headerSrc[c]));
  const rows = rowSrc.map((r) => {
    const cells = Array.isArray(r) ? r : r && typeof r === "object" ? Object.values(r) : [];
    const out = [];
    for (let c = 0; c < width; c++) out.push(normalizeCell(cells[c]));
    return out;
  });
  return { keys: outKeys, rows };
}

export function setCell(rows, rowIndex, colIndex, value) {
  return rows.map((r, i) => (i === rowIndex ? r.map((c, j) => (j === colIndex ? value : c)) : r));
}

export function setHeader(keys, colIndex, value) {
  return keys.map((k, j) => (j === colIndex ? value : k));
}

export function addRow(table) {
  return { keys: table.keys, rows: [...table.rows, table.keys.map(() => "")] };
}

export function addColumn(table) {
  return { keys: [...table.keys, ""], rows: table.rows.map((r) => [...r, ""]) };
}

// Keeps at least one row.
export function removeRow(table, rowIndex) {
  if (table.rows.length <= 1) return table;
  return { keys: table.keys, rows: table.rows.filter((_, i) => i !== rowIndex) };
}

// Keeps at least one column.
export function removeColumn(table, colIndex) {
  if (table.keys.length <= 1) return table;
  return {
    keys: table.keys.filter((_, j) => j !== colIndex),
    rows: table.rows.map((r) => r.filter((_, j) => j !== colIndex)),
  };
}

// RFC-4180-ish CSV: quoted fields, escaped quotes (""), CRLF/LF, and `,` or `;`
// delimiter (auto-detected from the first line — Turkish Excel exports use `;`).
// First line = headers. Returns null when there is nothing usable.
export function parseCSV(text) {
  if (typeof text !== "string") return null;
  const src = text.replace(/^﻿/, "");
  const firstLine = src.split(/\r?\n/)[0] || "";
  const delimiter =
    (firstLine.match(/;/g) || []).length > (firstLine.match(/,/g) || []).length ? ";" : ",";

  const records = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(field);
      records.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    records.push(row);
  }

  const nonEmpty = records.filter((r) => r.some((c) => c.trim() !== ""));
  if (nonEmpty.length === 0) return null;
  const [header, ...data] = nonEmpty;
  return normalizeTable(data.length ? data : [header.map(() => "")], header);
}

// Effective display options. Flat keys written by the right-rail "Tablo"
// accordion (showSearch, showPagination, showRowChoice, pageLength, sortColumn,
// sortOrder, theme, transparentBg) win; otherwise fall back to the legacy nested
// `state.options` the old config modal wrote. sortColumn -1 = no initial sort.
export function resolveOptions(state) {
  const s = state || {};
  const o = s.options || {};
  const keys = normalizeTable(s.data, s.keys).keys;

  let sortColumn;
  if (s.sortColumn !== undefined && s.sortColumn !== null && s.sortColumn !== "") {
    sortColumn = parseInt(s.sortColumn, 10);
  } else {
    // Legacy: initialSort held a header (often array-wrapped) — match by text.
    const idx = keys.indexOf(normalizeCell(o.initialSort));
    sortColumn = idx === -1 ? 0 : idx;
  }
  if (isNaN(sortColumn) || sortColumn >= keys.length) sortColumn = -1;

  const pageLength = parseInt(s.pageLength !== undefined ? s.pageLength : o.initialPageLength, 10);

  return {
    ...o,
    disableFilter: s.showSearch !== undefined ? !s.showSearch : !!o.disableFilter,
    disablePagination: s.showPagination !== undefined ? !s.showPagination : !!o.disablePagination,
    disableRowChoice: s.showRowChoice !== undefined ? !s.showRowChoice : !!o.disableRowChoice,
    initialPageLength: pageLength > 0 ? pageLength : 5,
    sortColumn,
    initialOrder: s.sortOrder || o.initialOrder || "descending",
    theme: s.theme || o.theme || "solid",
    // Opaque white by default; the author can opt into a see-through table.
    transparentBg: !!s.transparentBg,
  };
}
